// mission-archive.ts
//
// `avatar autopilot archive` (D5, R-13/R-14): moves a mission's directory out
// of `.avatar/missions/` so `resolveMission` stops listing it, and removes
// its worktree. Never deletes: `mission_home` is RENAMED into
// `.avatar/missions-archive/<id>-<timestamp>/`; the mission's
// `refs/heads/autopilot/<id>` ref is kept (forensics) and, before the worktree
// is removed, made to hold the worktree's final HEAD (SOL-01).
//
// "Bẩn" filter (phase-02 Req 3, H1) lives in mission-archive-dirty-paths.ts:
// init/controller output (`.avatar/`, `.claude/`, `.gitignore`) never counts
// as a user's work, so an `init`'d-never-`run` mission archives without `--force`.
import { promises as fs } from "node:fs";
import { dirname, join } from "node:path";
import { ensureDir, pathExists } from "../filesystem-helpers.js";
import { countUserDirtyPaths, isNotAGitRepositoryError } from "./mission-archive-dirty-paths.js";
import {
  defaultRunner,
  type GitRunner,
  preserveWorktreeHeadOnMissionRef,
  removeMissionWorktree,
} from "./mission-initializer-worktree.js";
import { type MachineLockFile, readMachineLock } from "./mission-lock.js";
import type { LockLivenessProbe } from "./process-liveness-probe.js";

export {
  ARCHIVE_IGNORED_DIRTY_PREFIXES,
  countUserDirtyPaths,
} from "./mission-archive-dirty-paths.js";

export interface ArchiveMissionInput {
  workspaceRoot: string;
  missionId: string;
  missionHome: string;
  /** `<missionHome>/workspace` — may not exist (init failed before the worktree step). */
  worktreePath: string;
  /** Source repo the worktree came from — needed to run `git worktree remove` from the MAIN repo. */
  sourceRepo?: string;
  force?: boolean;
  lockPath: string;
  liveness: LockLivenessProbe;
  gitRunner?: GitRunner;
  now?: () => Date;
}

export interface ArchiveMissionRefusal {
  ok: false;
  reason: "lock_held_by_this_mission" | "worktree_dirty" | "git_status_failed";
  message: string;
  dirtyCount?: number;
}

export interface ArchiveMissionSuccess {
  ok: true;
  mission_id: string;
  archived_to: string;
  worktree_removed: boolean;
  branch_kept: boolean;
  /** SOL-01: ref holding the worktree's final HEAD (absent when no worktree / unborn HEAD). */
  head_ref?: string;
}

export type ArchiveMissionResult = ArchiveMissionRefusal | ArchiveMissionSuccess;

function timestampSuffix(now: Date): string {
  return now.toISOString().replace(/[-:]/g, "").replace(/\..+/, "").replace("T", "");
}

/**
 * Refuses (nothing changes) when: the machine lock names THIS mission and its
 * pid is alive; or the worktree has user-dirty changes and `force` was not
 * passed. Otherwise removes the worktree (when present, from the main repo)
 * and renames `missionHome` into `.avatar/missions-archive/<id>-<timestamp>/`.
 * Never deletes the mission's branch ref; only moves it forward to a detached
 * worktree HEAD (see preserveWorktreeHeadOnMissionRef).
 */
// Req 7 (phase 02): `init` may create `missions/<id>/` and THEN fail (e.g. a
// preflight/config error surfacing after `ensureDir(controlRoot)`), leaving a
// half-initialized mission dir with nothing pointing the operator at
// cleanup. Pure diff so it is testable without mocking the filesystem.
export function formatInitLeftoverHints(before: string[], after: string[]): string[] {
  const beforeSet = new Set(before);
  return after
    .filter((id) => !beforeSet.has(id))
    .map((id) => `Mission dở ${id} — dọn bằng: avatar autopilot archive --mission ${id}`);
}

/** `.avatar/missions/` entries at a point in time — ENOENT (no dir yet) reads as empty, never throws. */
export async function listMissionIdsForHint(workspaceRoot: string): Promise<string[]> {
  const dir = join(workspaceRoot, ".avatar", "missions");
  if (!(await pathExists(dir))) return [];
  const entries = await fs.readdir(dir, { withFileTypes: true }).catch(() => []);
  return entries.filter((e) => e.isDirectory()).map((e) => e.name);
}

export async function archiveMission(input: ArchiveMissionInput): Promise<ArchiveMissionResult> {
  const now = input.now ?? (() => new Date());
  const runner = input.gitRunner ?? defaultRunner;

  const lock: MachineLockFile | undefined = await readMachineLock(input.lockPath);
  if (lock && lock.missionId === input.missionId && (await input.liveness.isAlive(lock.pid))) {
    return {
      ok: false,
      reason: "lock_held_by_this_mission",
      message:
        `Mission "${input.missionId}" đang chạy (lock ${input.lockPath}, pid ${lock.pid} còn sống). ` +
        "Dừng bằng `avatar autopilot pause`/Ctrl-C rồi thử lại, hoặc `avatar autopilot unlock` nếu lock đã mồ côi.",
    };
  }

  const worktreeExists = await pathExists(input.worktreePath);
  if (worktreeExists && !input.force) {
    const status = await runner(
      ["status", "--porcelain=v1", "-z", "--untracked-files=all"],
      input.worktreePath,
    );
    if (status.status === 0) {
      const dirtyCount = countUserDirtyPaths(status.stdout);
      if (dirtyCount > 0) {
        return {
          ok: false,
          reason: "worktree_dirty",
          dirtyCount,
          message:
            `Worktree ${input.worktreePath} có ${dirtyCount} file thay đổi của người dùng chưa commit. ` +
            "Dùng `--force` để archive và MẤT các thay đổi đó, hoặc commit/dọn tay trước.",
        };
      }
    } else if (!isNotAGitRepositoryError(status.stderr)) {
      // Re-review 2026-09-26 (D-02): any OTHER failure (stale index.lock, old
      // git, …) means the worktree could not be proven clean — refuse rather
      // than `worktree remove --force` over work nobody inspected.
      return {
        ok: false,
        reason: "git_status_failed",
        message:
          `Không kiểm được worktree ${input.worktreePath} (git status exit ${status.status}: ${status.stderr.trim()}). ` +
          "Sửa lỗi git rồi thử lại, hoặc dùng `--force` để archive và MẤT thay đổi chưa commit (nếu có).",
      };
    }
    // Không phải git repo (worktree hỏng/dở) — không có gì để bảo vệ, cứ gỡ.
  }

  let worktreeRemoved = false;
  let headRef: string | undefined;
  if (worktreeExists) {
    const mainRepo =
      input.sourceRepo ??
      dirname((await runner(["rev-parse", "--git-common-dir"], input.worktreePath)).stdout.trim());
    // SOL-01: the final HEAD must sit on a ref BEFORE the worktree goes — a
    // detached HEAD (missions init'd before the fix) is reachable from nothing
    // once `worktree remove` runs. Throws ⇒ nothing removed.
    headRef = await preserveWorktreeHeadOnMissionRef(input.worktreePath, input.missionId, runner);
    await removeMissionWorktree(mainRepo, input.worktreePath, runner);
    worktreeRemoved = true;
  }

  const archiveRoot = join(input.workspaceRoot, ".avatar", "missions-archive");
  await ensureDir(archiveRoot);
  const archivedTo = join(archiveRoot, `${input.missionId}-${timestampSuffix(now())}`);
  try {
    await fs.rename(input.missionHome, archivedTo);
  } catch (err) {
    // Worktree có thể đã gỡ ở trên ⇒ nói rõ trạng thái nửa chừng, không xoá gì thêm.
    throw new Error(
      `archive: đã ${worktreeRemoved ? "gỡ worktree" : "bỏ qua worktree (không có)"} nhưng không chuyển được ` +
        `${input.missionHome} → ${archivedTo} (${(err as Error).message}). Thư mục mission vẫn nguyên ở chỗ cũ; ` +
        "chuyển tay bằng `mv` hoặc chạy lại `avatar autopilot archive`.",
    );
  }

  return {
    ok: true,
    mission_id: input.missionId,
    archived_to: archivedTo,
    worktree_removed: worktreeRemoved,
    branch_kept: true,
    ...(headRef !== undefined ? { head_ref: headRef } : {}),
  };
}
